import { memo, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { TeamType } from '@typings/participant';
import { ParticipantPosition } from '@typings/participant';
import { useParticipantDataById } from '@redux/selectors/participants';
import { useUserLikeStatus, useUserLikesCount } from '@redux/selectors/likes';
import { useLikeHostMutation } from '@redux/queries/like';

import RightSideParticipant from './RightSideParticipant';
import LeftSideParticipant from './LeftSideParticipant';

const Container = styled.div`
  position: relative;
`;

type Props = {
  participantId?: string;
  side?: TeamType;
  position?: ParticipantPosition;
  size: {
    height: number;
    width: number;
  };
  speakingUIDs?: Record<string, number>;
  emojisSequence?: string[];
  isStatusesShown?: boolean;
  onEmojiFinish?: () => void;
};

const HostView = ({
  participantId,
  side,
  position,
  size,
  speakingUIDs = {},
  emojisSequence,
  isStatusesShown,
  onEmojiFinish,
}: Props) => {
  const participant = useParticipantDataById(participantId);
  const { isHost, isCoHost, currentShowId } = useAgoraEngine();
  // const navigate = useNavigate();
  const isSpeaking = useMemo(
    () =>
      isHost || isCoHost
        ? speakingUIDs['0'] === 0
        : !!participant && speakingUIDs[participant?.uid] !== undefined,
    [participant, isHost, isCoHost, speakingUIDs],
  );
  const isLiked = useUserLikeStatus({
    showId: currentShowId,
    userId: participant?.profileId,
  });
  const likesCount = useUserLikesCount({
    showId: currentShowId,
    userId: participant?.profileId,
  });

  const [like] = useLikeHostMutation();

  const sendSetLikeRequest = useCallback(
    (liked: boolean) => {
      if (!participant?.profileId || !currentShowId) {
        return;
      }

      like({
        userId: participant?.profileId,
        showId: currentShowId,
        weight: liked ? 1 : 0,
      });
    },
    [participant?.profileId, currentShowId, like],
  );

  if (!participantId || !participant) {
    return null;
  }

  return (
    <Container>
      {position === ParticipantPosition.Right ? (
        <RightSideParticipant
          key={participant?.id}
          participantId={participant?.id || null}
          side={side}
          size={size}
          emojisSequence={emojisSequence}
          isSpeaking={isSpeaking}
          isStatusesShown={isStatusesShown}
          isLiked={isLiked}
          likesCount={likesCount}
          onLike={sendSetLikeRequest}
          onEmojiFinish={onEmojiFinish}
        />
      ) : (
        <LeftSideParticipant
          key={participant?.id}
          participantId={participant?.id || null}
          side={side}
          size={size}
          emojisSequence={emojisSequence}
          isSpeaking={isSpeaking}
          isStatusesShown={isStatusesShown}
          isLiked={isLiked}
          likesCount={likesCount}
          onLike={sendSetLikeRequest}
          onEmojiFinish={onEmojiFinish}
        />
      )}
    </Container>
  );
};

export default memo(HostView);
